import type { ReactElement } from 'react';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import type { AddFromAlertScope } from '@/hooks/api/useAddPolicyExceptionFromAlert';
import { AlertDetailCard } from './AlertDetailCard';
import { AlertDetailCards } from './AlertDetailCards';
import type { AlertFactsStripVariant } from './AlertFactsStrip';
import { AlertActionsBar } from './AlertActionsBar';
import { AlertFeedbackFields } from './AlertFeedbackFields';
import { AlertFactsStrip } from './AlertFactsStrip';
import { AlertSessionId } from './session-timeline/AlertSessionId';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';

interface AlertDetailContentProps {
  alert: GroupedAlert;
  className?: string;
  organizationId?: string;
  scope?: AddFromAlertScope;
  alertKey?: string;
  /**
   * KIR-4281 — `'occurrence'` renders the panel for ONE occurrence. Both
   * layouts take it: both drop the group actions from the actions bar and
   * Status / Comment, and the representative-value hints in
   * {@link AlertSecretDetails}. `endpointLabel` stays cards-only — the legacy
   * layout renders a facts strip only when `standalone`, and without it.
   */
  variant?: AlertFactsStripVariant;
  /** KIR-4281 — see {@link AlertDetailCards}; forwarded to the cards layout only. */
  endpointLabel?: string | null;
  /** Shows "Show Instances" in the actions bar; omitted on the occurrences page. */
  onShowOccurrences?: () => void;
  /**
   * The alert window: the facts strip carries the table row's File and First
   * seen, and the session ID (with copy) sits at the top right. The legacy
   * layout gets the strip above its card for the same facts.
   */
  standalone?: boolean;
}

/**
 * KIR-4481 — chooses the alert detail presentation: the redesigned grouped-cards
 * panel when `alerts_grouped_cards` is on, else the legacy flat {@link
 * AlertDetailCard}. Keeps the flag decision in one place so every mount point
 * (both alerts DataTables, the legacy table, and the occurrences modal) stays in
 * lockstep. Both put the {@link AlertActionsBar} (actions, Session Timeline,
 * Show Instances) at the top: the cards inside their title bar, the legacy
 * card above itself.
 */
export function AlertDetailContent({
  alert,
  className,
  organizationId,
  scope,
  alertKey,
  variant,
  endpointLabel,
  onShowOccurrences,
  standalone,
}: AlertDetailContentProps): ReactElement {
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ALERTS_GROUPED_CARDS,
  ]);
  const groupedCardsEnabled =
    flags[flagsmithFeatureFlags.ALERTS_GROUPED_CARDS] === true;

  if (groupedCardsEnabled) {
    return (
      <AlertDetailCards
        alert={alert}
        className={className}
        organizationId={organizationId}
        scope={scope}
        alertKey={alertKey}
        {...(variant !== undefined ? { variant } : {})}
        {...(endpointLabel !== undefined ? { endpointLabel } : {})}
        {...(onShowOccurrences ? { onShowOccurrences } : {})}
        {...(standalone ? { standalone } : {})}
      />
    );
  }
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <AlertActionsBar
          alert={alert}
          organizationId={organizationId}
          {...(variant !== undefined ? { variant } : {})}
          {...(onShowOccurrences ? { onShowOccurrences } : {})}
        />
        {standalone && alert.sessionLink && (
          <AlertSessionId
            sessionId={alert.sessionLink.sessionId}
            className="ml-auto"
          />
        )}
      </div>
      {standalone && (
        <AlertFactsStrip
          alert={alert}
          standalone
          riskBadges={<SeverityBadge riskLevel={alert.severity} showIcon />}
        />
      )}
      <AlertDetailCard
        alert={alert}
        className={className}
        organizationId={organizationId}
        scope={scope}
        alertKey={alertKey}
        {...(variant !== undefined ? { variant } : {})}
      />
      {variant !== 'occurrence' && organizationId && (
        <AlertFeedbackFields alert={alert} organizationId={organizationId} />
      )}
    </div>
  );
}

AlertDetailContent.displayName = 'AlertDetailContent';
